import { h, Fragment } from "preact";
import Router, { RoutableProps } from "preact-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import { resolvePath } from "../../../../api";
import { generate, getLabelIds, labelPreviewUrl, labelsMetadata } from "../../../../api/label-creator";
import {
    createTemplateLabel,
    deleteTemplateLabel,
    editTemplateLabel,
    getChildLabels,
    getLabels,
    getTemplateLabel,
    Label,
} from "../../../../api/product-api";
import { baseUrl, route } from "../../../../App";
import { Snackbar, SnackbarShowCommand } from "../../../../components/Snackbar";
import { TemplatePrint, parseTapeType } from "../../../../components/TemplatePrint";
import { useCommand } from "../../../../hooks";
import { generateExampleFieldsForLabel, getExampleSerialNumber, parseToDateAndFormatIfExists } from "../../../../utils";
import { predefinedMetadataKeys, TemplateMetadataDisplay } from "../../../../components/TemplateMetadataDisplay";
import "./styles.scss";
import { LabelMetadataEditorHelp } from "../../LabelMetadataEditorHelp";
import { LabelMetadataFieldEdit } from "../../LabelMetadataFieldEdit";
import { LabelSVGInteractive } from "../../LabelSVGInteractive";
import { SimpleList } from "../../SimpleList";
import { LabelMetadataElementList } from "../../LabelMetadataElementList";
import { FileInput } from "../../../../components/FileInput";

function pass<T>(fn: () => T) {
    return fn();
}

function LabelMetadataInput(props: {
    style: string;
    placeholder: string;
    property: string;
    metadata: { [key: string]: string };
    onChange: (property: string, value: string) => void;
    knownValues?: string[];
    propertyMap?: {
        key: string;
    };
    customValue?: string;
}) {
    const [intermediateValue, setIntermediateValue] = useState("");

    const updateValue = useCallback(
        (newValue: string) => {
            if (props.propertyMap) {
                const value = new URLSearchParams(props.metadata[props.property]);

                if (newValue) {
                    value.set(props.propertyMap.key, newValue);
                } else {
                    value.delete(props.propertyMap.key);
                }

                newValue = value.toString();
            }

            props.onChange(props.property, newValue);
        },
        [props.onChange],
    );

    useMemo(() => {
        let value = props.metadata[props.property];

        if (props.propertyMap) {
            value = new URLSearchParams(value).get(props.propertyMap.key) ?? "";
        }

        setIntermediateValue(value);
    }, [props.property, props.metadata]);

    return (
        <>
            {!props.knownValues ? null : (
                <select
                    style={props.style}
                    value={props.knownValues.includes(intermediateValue) ? intermediateValue : ""}
                    onChange={e => {
                        setIntermediateValue(e.currentTarget ? e.currentTarget.value : intermediateValue);
                        updateValue(e.currentTarget.value);
                    }}
                >
                    {props.customValue ? (
                        <option default value="">
                            {props.customValue}
                        </option>
                    ) : null}
                    {props.knownValues.map(v => (
                        <option value={v}>{v}</option>
                    ))}
                </select>
            )}
            {!props.knownValues || !props.knownValues.includes(intermediateValue) ? (
                <input
                    style={props.style}
                    placeholder={props.placeholder}
                    value={intermediateValue}
                    onInput={e => {
                        setIntermediateValue(e.currentTarget.value);
                    }}
                    onChange={e => {
                        setIntermediateValue(e.currentTarget.value);
                        updateValue(e.currentTarget.value);
                    }}
                />
            ) : null}
        </>
    );
}

function LabelMetadataElementEdit(props: {
    metadata: { readonly [key: string]: string };
    onChange: (property: string, newValue: string | null) => void;
    onReplace: (oldProperty: string, newProperty: string, newValue: string) => void;
    labelSvg: string;
    labelIds: string[];
}) {
    const [newElementName, setNewElementName] = useState("");
    const [selectedElement, setSelectedElement] = useState("");

    return (
        <>
            <div style="margin-top: 20px;">
                <h2>Podgląd szablonu</h2>
                <div style="margin-bottom: 10px;">
                    <LabelSVGInteractive
                        svg={props.labelSvg}
                        highlight={selectedElement.split("-").slice(2).join("-")}
                    />
                </div>
            </div>
            <div style="padding: 10px 0px;">
                <div>
                    <h2>Elementy zmienne szablonu</h2>
                    <select
                        type="text"
                        placeholder="Nazwa elementu..."
                        value={newElementName}
                        onChange={e => setNewElementName(e.currentTarget.value)}
                    >
                        {props.labelIds
                            .filter(
                                v =>
                                    v.indexOf("-") < 0 &&
                                    Object.keys(props.metadata)
                                        .filter(v => v.startsWith("element-"))
                                        .map(v => v.split("-").slice(2))
                                        .join("-")
                                        .indexOf(v) < 0,
                            )
                            .map(v => (
                                <option value={v}>{v}</option>
                            ))}
                    </select>
                    <button
                        style="margin-left: 10px;"
                        onClick={() => {
                            if (!newElementName) {
                                return;
                            }

                            setSelectedElement("element-text-" + newElementName);
                            props.onChange("element-text-" + newElementName, "");
                        }}
                    >
                        Dodaj element
                    </button>
                </div>
                <div style="display: flex">
                    <LabelMetadataElementList
                        style="margin-top: 10px; margin-right: 10px;"
                        header="Dodane elementy"
                        emptyListMessage="Brak dodanych elementów"
                        metadata={props.metadata}
                        onChange={setSelectedElement}
                        value={selectedElement}
                        checkValidity={item => props.labelIds.indexOf(item.split("-").slice(2).join("-")) >= 0}
                        validityError="Element o tej nazwie nie wsytępuje w pliku SVG"
                    />

                    {/* <div style="margin-top: 10px; margin-right: 10px; border: 1px solid #4f4f4f; padding-bottom: 10px">
                    <h3 style="padding: 4px 10px; margin: 0; background: #4f4f4f">Dodane elementy</h3>
                    {pass(() => {
                        const elements = Object.keys(props.metadata).filter(v => v.startsWith("element-"));

                        if (elements.length === 0) {
                            return <div style="padding: 2px 10px; margin: 2px 0px;">Brak dodanych elementów</div>;
                        }

                        return <div class="list">
                            {Object.keys(props.metadata).filter(v => v.startsWith("element-")).map(v =>
                                <div
                                    class={"primary item" + (selectedElement === v ? " selected" : "")}
                                    style="padding: 4px 10px; margin: 2px 0px; display: flex; align-items: center;"
                                    onClick={() => setSelectedElement(v)}
                                >
                                    <span class="chip secondary" style="margin-right: 10px;">{v.split("-").slice(1, 2)}</span>
                                    <span>{v.split("-").slice(2).join("-")}</span>
                                    {props.metadata["description-" + v] ? <span style="color: #afafaf; font-size: 10px; margin-left: 5px">{props.metadata["description-" + v]}</span> : null}
                                </div>)
                            }
                        </div>
                    })}
                </div> */}
                    {selectedElement ? (
                        <div>
                            <div style="margin-top: 20px; display: flex; justify-content: center; flex-direction: column">
                                <h3>
                                    <span
                                        class={
                                            props.labelIds.indexOf(selectedElement.split("-").slice(2).join("-")) < 0
                                                ? "tooltip tooltip-error"
                                                : ""
                                        }
                                        style={{
                                            backgroundColor:
                                                props.labelIds.indexOf(selectedElement.split("-").slice(2).join("-")) <
                                                0
                                                    ? "var(--palette-error-dark)"
                                                    : "",
                                            textAlign: "left",
                                        }}
                                        data-tooltip="Element o tej nazwie nie wsytępuje w pliku SVG"
                                    >
                                        Element '{selectedElement.split("-").slice(2).join("-")}':
                                    </span>
                                </h3>
                                <label>
                                    <span
                                        style={{
                                            width: "200px",
                                            textAlign: "right",
                                        }}
                                        data-tooltip="Element o tej nazwie nie wsytępuje w pliku SVG"
                                    >
                                        Rodzaj elementu:
                                    </span>
                                    <select
                                        style="margin-left: 10px"
                                        type="text"
                                        value={selectedElement.split("-")[1]}
                                        onChange={e => {
                                            const elementInfo = selectedElement.split("-");
                                            const type = e.currentTarget.value;
                                            const name = elementInfo.slice(2).join("-");

                                            props.onReplace(
                                                selectedElement,
                                                "element-" + type + "-" + name,
                                                props.metadata[selectedElement],
                                            );
                                            setSelectedElement("element-" + type + "-" + name);
                                        }}
                                    >
                                        {["text", "datamatrix", "qrcode"].map(v => (
                                            <option value={v}>{v}</option>
                                        ))}
                                    </select>
                                </label>
                                <div style="margin-top: 10px; display: flex; align-items: center;">
                                    <label>
                                        <span style="width: 200px; text-align: right">Opis elementu:</span>
                                        <LabelMetadataInput
                                            key={"description-" + selectedElement}
                                            style="margin-left: 10px;"
                                            placeholder="Opis elementu..."
                                            knownValues={[
                                                "Model",
                                                "Data produkcji",
                                                "Numer Seryjny",
                                                "Rewizja",
                                                "GTIN 13",
                                                "GTIN 14",
                                                "Numer LOT",
                                                "GS1 Data Matrix",
                                            ]}
                                            customValue="Własny opis"
                                            property={"description-" + selectedElement}
                                            metadata={props.metadata}
                                            onChange={(prop, value) => {
                                                props.onChange(prop, value || null);
                                            }}
                                        />
                                    </label>
                                </div>
                                {selectedElement.split("-").slice(1, 2)[0] === "text" ? (
                                    <div style="margin-top: 10px; display: flex; align-items: center;">
                                        <label>
                                            <span style="width: 200px; display: flex; justify-content: flex-end">
                                                <span
                                                    class="tooltip tooltip-center"
                                                    data-tooltip="Maksymalna ilość znaków jaką można wpisać w ten element. Jeśli pole jest niewypełnione wtedy nie ma ograniczenia."
                                                >
                                                    Maksymalna ilość znaków:
                                                </span>
                                            </span>
                                            <LabelMetadataInput
                                                key={"properties-" + selectedElement}
                                                style="margin-left: 10px; width: 200px;"
                                                placeholder="Maksymalna ilość znaków..."
                                                property={"properties-" + selectedElement}
                                                metadata={props.metadata}
                                                propertyMap={{
                                                    key: "maxCharacters",
                                                }}
                                                onChange={(prop, value) => {
                                                    props.onChange(prop, value || null);
                                                }}
                                            />
                                        </label>
                                    </div>
                                ) : null}
                                <div style="margin-top: 10px; display: flex; align-items: center;">
                                    <label>
                                        <span style="width: 200px; text-align: right">Przykładowa treść elementu:</span>
                                        <LabelMetadataInput
                                            key={selectedElement}
                                            style="margin-left: 10px; width: 200px"
                                            placeholder="Przykładowa treść elementu..."
                                            property={selectedElement}
                                            metadata={props.metadata}
                                            onChange={(prop, value) => {
                                                props.onChange(prop, value);
                                            }}
                                        />
                                    </label>
                                    {/* <LabelMetadataFieldEdit metadata={label.metadata?.metadata ?? {}} property={v} onChange={(value) => {
                                setLabel({
                                    ...label,
                                    metadata: {
                                        ...label.metadata,
                                        metadata: {
                                            ...label.metadata?.metadata,
                                            [v]: value
                                        }
                                    }
                                });
                            }}/> */}
                                </div>
                                <div style="margin-top: 10px; display: flex; align-items: center;">
                                    <span style="width: 200px; text-align: right">Usuń element:</span>
                                    <button
                                        class="icon flat error"
                                        style="margin-left: 10px; width: 24px; height: 24px; font-size: 18px"
                                        onClick={() => {
                                            const elements = Object.keys(props.metadata).filter(v =>
                                                v.startsWith("element-"),
                                            );
                                            const nextElementIndex = elements.indexOf(selectedElement) + 1;

                                            setSelectedElement(elements[nextElementIndex] ?? "");

                                            props.onChange(selectedElement, null);
                                            props.onChange("description-" + selectedElement, null);
                                        }}
                                    >
                                        delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </>
    );
}

function TestLabelPreview({
    file,
    label,
    labelMetadata,
}: {
    file: File | null;
    label: Label;
    labelMetadata: { [key: string]: string };
}) {
    const [src, setSrc] = useState("");

    useEffect(() => {
        if (!file || !label || !labelMetadata) {
            setSrc("");
            return;
        }

        generate(
            file,
            generateExampleFieldsForLabel({ ...labelMetadata, ...label.metadata?.metadata }),
            label.metadata?.metadata,
        ).then(v => setSrc(v));
    }, [file, label, labelMetadata]);

    return !src ? null : <img style="max-width: 800px; max-height: 500px" src={src} />;
}

export function LabelTemplateEditPage(props: RoutableProps & { id?: string; cloneId?: string }) {
    const [label, setLabel] = useState(null as null | Label);
    const [labelIds, setLabelIds] = useState([] as string[]);
    const [labelMetadata, setLabelMetadata] = useState({} as { [key: string]: string });
    const [newEntryName, setNewEntryName] = useState("");
    const [labelSvg, setLabelSvg] = useState("");
    const showSnackbarCommand = useCommand<SnackbarShowCommand>();
    const templateMetadataDisplayClear = useCommand();
    const templateMetadataDisplaySetMetadata = useCommand<[metadata: { [key: string]: string }]>();
    const [childLabels, setChildLabels] = useState(null as null | Label[]);
    const [testLabelFile, setTestLabelFile] = useState(null as null | File);
    const [testLabelFileReset, setTestLabelFileReset] = useState(0);

    useEffect(() => {
        if (props.id) {
            getTemplateLabel(+props.id).then(async label => {
                setLabel(label);
            });
        } else if (props.cloneId) {
            getTemplateLabel(+props.cloneId).then(async label => {
                delete (label as any).id;
                delete (label as any).createdAt;
                delete (label as any).creator;
                setLabel(label);
            });
        } else {
            setLabel({
                metadata: {
                    metadata: {},
                },
            } as Label);
        }
    }, [props.id, props.cloneId]);

    useEffect(() => {
        if (!props.id) {
            setChildLabels([]);
            return;
        }

        getChildLabels(+props.id).then(v => setChildLabels(v));
    }, [props.id]);

    useEffect(() => {
        if (!label?.path) {
            setLabelIds([]);
            setLabelMetadata({});
            return;
        }

        getLabelIds(label.path)
            .then(async label => {
                setLabelIds(label);
            })
            .catch(() => {
                showSnackbarCommand.run("Nie udało się pobrać danych naklejki", "error", "error");
                setLabelIds([]);
            });

        labelsMetadata(label.path)
            .then(async label => {
                setLabelMetadata(label);
            })
            .catch(() => {
                showSnackbarCommand.run("Nie udało się pobrać danych naklejki", "error", "error");
                setLabelIds([]);
            });

        fetch(labelPreviewUrl(label.path)).then(v =>
            v.text().then(t => {
                setLabelSvg(t);

                const length = parseFloat((/viewBox="\d+ \d+ (\d+) \d+"/i.exec(t) ?? ["", ""])[1]);

                if (label.metadata?.metadata) {
                    label.metadata.metadata["label-length"] = (length / 14 + 2).toPrecision(2) + "mm";
                }
            }),
        );
    }, [label?.path]);

    if (!label) {
        return (
            <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; width: 100%; height: 100%">
                <div>Pobieranie danych...</div>
                <div class="loader"></div>
            </div>
        );
    }

    return (
        <div class="label-edit-page">
            <Snackbar doShow={showSnackbarCommand} />
            <div class="order-items paper" style="margin-top: 10px;">
                <h1>{label.id ? "Szablon naklejki: " + label.name : "Nowy szablon naklejki"}</h1>
                <div style="display: flex">
                    <div style="flex: 1 0">
                        <div>
                            <label>
                                <span style="width: 120px; text-align: right">Nazwa szablonu:</span>
                                {props.id ? (
                                    <input
                                        style="margin-left: 5px; background: #353535; color: #8f8f8f"
                                        disabled
                                        type="text"
                                        value={label.name}
                                    />
                                ) : (
                                    <input
                                        style="margin-left: 5px"
                                        type="text"
                                        value={label.name}
                                        onChange={e => {
                                            setLabel({
                                                ...label,
                                                name: e.currentTarget.value,
                                            });
                                        }}
                                    />
                                )}
                            </label>
                        </div>
                        <div style="margin-top: 10px;">
                            <label style="width: 100%">
                                <span style="width: 120px; text-align: right">Ścieżka:</span>
                                <input
                                    style="margin-left: 5px; max-width: 800px; width: 100%;"
                                    type="text"
                                    value={label.path}
                                    onChange={e => {
                                        setLabel({
                                            ...label,
                                            path: e.currentTarget.value.trim(),
                                        });
                                    }}
                                />
                            </label>
                        </div>
                        <div style="margin-top: 10px;">
                            <label>
                                <span style="width: 120px; text-align: right">Rewizja:</span>
                                <input
                                    style="margin-left: 5px"
                                    type="text"
                                    value={label.revision}
                                    onChange={e => {
                                        setLabel({
                                            ...label,
                                            revision: e.currentTarget.value,
                                        });
                                    }}
                                />
                            </label>
                        </div>
                        <div style="margin-top: 10px;">
                            <label>
                                <span style="width: 120px; text-align: right">Drukarka:</span>
                                <select
                                    style="margin-left: 5px"
                                    type="text"
                                    value={label.metadata?.metadata["printer"] ?? ""}
                                    onChange={e => {
                                        setLabel({
                                            ...label,
                                            metadata: {
                                                ...label.metadata,
                                                metadata: {
                                                    ...label.metadata?.metadata,
                                                    printer: e.currentTarget.value,
                                                },
                                            },
                                        });
                                    }}
                                >
                                    {predefinedMetadataKeys["printer"].values!.map(v => (
                                        <option value={v}>{v}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        <div style="margin-top: 10px;">
                            <label>
                                <span style="width: 120px; text-align: right">Szerokość taśmy:</span>
                                <select
                                    style="margin-left: 5px"
                                    type="text"
                                    value={label.metadata?.metadata["tape-width"] ?? ""}
                                    onChange={e => {
                                        setLabel({
                                            ...label,
                                            metadata: {
                                                ...label.metadata,
                                                metadata: {
                                                    ...label.metadata?.metadata,
                                                    "tape-width": e.currentTarget.value,
                                                },
                                            },
                                        });
                                    }}
                                >
                                    {predefinedMetadataKeys["tape-width"].values!.map(v => (
                                        <option value={v}>{v}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        <div style="margin-top: 10px;">
                            <label>
                                <span style="width: 120px; text-align: right">Rodzaj taśmy:</span>
                                <select
                                    style="margin-left: 5px"
                                    type="text"
                                    value={label.metadata?.metadata["tape-type"] ?? ""}
                                    onChange={e => {
                                        setLabel({
                                            ...label,
                                            metadata: {
                                                ...label.metadata,
                                                metadata: {
                                                    ...label.metadata?.metadata,
                                                    "tape-type": e.currentTarget.value,
                                                },
                                            },
                                        });
                                    }}
                                >
                                    {predefinedMetadataKeys["tape-type"].values!.map(v => (
                                        <option value={v}>{v}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        <div style="margin-top: 10px;">
                            <label>
                                <span style="width: 120px; text-align: right">Wysoka jakość:</span>
                                <select
                                    style="margin-left: 5px"
                                    type="text"
                                    value={label.metadata?.metadata["high-quality"] ?? ""}
                                    onChange={e => {
                                        setLabel({
                                            ...label,
                                            metadata: {
                                                ...label.metadata,
                                                metadata: {
                                                    ...label.metadata?.metadata,
                                                    "high-quality": e.currentTarget.value,
                                                },
                                            },
                                        });
                                    }}
                                >
                                    {predefinedMetadataKeys["high-quality"].values!.map(v => (
                                        <option value={v}>{v}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        <div style="margin-top: 10px;">
                            <label>
                                <span style="width: 120px; text-align: right">Długość naklejki:</span>
                                <input
                                    style="margin-left: 5px; background: #353535; color: #8f8f8f"
                                    type="text"
                                    value={
                                        !label.metadata?.metadata["label-length"]
                                            ? "?"
                                            : `${
                                                  isNaN(parseFloat(label.metadata?.metadata["label-length"]))
                                                      ? label.metadata?.metadata["label-length"]
                                                      : `${parseFloat(label.metadata?.metadata["label-length"]).toFixed(0)}mm`
                                              } (${(parseFloat(label.metadata?.metadata["label-length"]) - 2) * 14}px)`
                                    }
                                    disabled
                                />
                            </label>
                        </div>
                    </div>
                    <div style="margin-left: 10px;">
                        <h2>Podgląd taśmy</h2>
                        <div>
                            <img
                                style="max-width: 200px"
                                src={`${resolvePath("images/tapes/")}${parseTapeType(label.metadata?.metadata["tape-width"], label.metadata?.metadata["tape-type"] ?? "")}`}
                            />
                        </div>
                    </div>
                </div>
                <LabelMetadataElementEdit
                    labelIds={labelIds}
                    labelSvg={labelSvg}
                    metadata={label.metadata?.metadata ?? {}}
                    onChange={(prop, value) => {
                        if (value !== null) {
                            setLabel(prev =>
                                prev
                                    ? {
                                          ...prev,
                                          metadata: {
                                              ...prev.metadata,
                                              metadata: {
                                                  ...prev.metadata?.metadata,
                                                  [prop]: value,
                                              },
                                          },
                                      }
                                    : prev,
                            );
                        } else {
                            setLabel(prev =>
                                prev
                                    ? (() => {
                                          const newLabel = {
                                              ...prev,
                                              metadata: {
                                                  ...prev.metadata,
                                                  metadata: {
                                                      ...prev.metadata?.metadata,
                                                  },
                                              },
                                          };

                                          delete newLabel.metadata.metadata[prop];
                                          return newLabel;
                                      })()
                                    : prev,
                            );
                        }
                    }}
                    onReplace={(oldProp, newProp, value) => {
                        setLabel(prev =>
                            prev
                                ? (() => {
                                      const newLabel = {
                                          ...prev,
                                          metadata: {
                                              ...prev.metadata,
                                              metadata: {} as { [key: string]: string },
                                          },
                                      };

                                      if (!prev.metadata?.metadata) {
                                          return newLabel;
                                      }

                                      for (const prop in prev.metadata?.metadata) {
                                          if (prop === oldProp) {
                                              newLabel.metadata.metadata[newProp] = value;
                                              continue;
                                          }

                                          newLabel.metadata.metadata[prop] = prev.metadata.metadata[prop];
                                      }

                                      return newLabel;
                                  })()
                                : prev,
                        );
                    }}
                />
                <div style="margin-top: 20px;">
                    <h2>Podgląd wydruku</h2>
                    <div style="margin-bottom: 10px;">
                        {testLabelFile && (
                            <TestLabelPreview file={testLabelFile} label={label} labelMetadata={labelMetadata} />
                        )}
                        <img
                            style="max-width: 800px; max-height: 500px"
                            src={generate(
                                label.path,
                                generateExampleFieldsForLabel({ ...labelMetadata, ...label.metadata?.metadata }),
                                label.metadata?.metadata,
                            )}
                        />
                    </div>
                </div>
                {!props.id && (
                    <div class="accordion" style="margin-top: 10px;">
                        <div>
                            <input type="radio" name="testing-accordion" />
                            <input type="radio" name="testing-accordion" />
                            <div class="header">Testowanie</div>
                            <div class="content py-4" style="flex-direction: column">
                                <span class="mr-1">Plik naklejki do testowania:</span>
                                <div class="flex flex-row gap-2">
                                    <form>
                                        <FileInput
                                            style="width: 400px;"
                                            accept=".svg"
                                            reset={testLabelFileReset}
                                            onChange={files => {
                                                setTestLabelFile(files[0] ?? null);

                                                const reader = new FileReader();
                                                reader.onload = () => {
                                                    const svgText = reader.result as string;
                                                    setLabelSvg(svgText);

                                                    // Find all ids in the SVG
                                                    setLabelIds(
                                                        svgText
                                                            .match(/id="([^"]+)"/g)
                                                            ?.map(v => v.replace(/id="/, "").replace(/"/, "")) ?? [],
                                                    );
                                                };
                                                reader.readAsText(files[0]);
                                            }}
                                        />
                                    </form>
                                    <button
                                        class="error"
                                        onClick={() => {
                                            setTestLabelFile(null);
                                            setTestLabelFileReset(prev => prev + 1);
                                            setLabelSvg("");
                                        }}
                                    >
                                        Wyczyść
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div class="accordion" style="margin-top: 10px;">
                    <div>
                        <input type="radio" name="advanced-accordion" />
                        <input type="radio" name="advanced-accordion" />
                        <div class="header">Zaawansowane</div>
                        <div class="content" style="flex-direction: column">
                            <div style="margin-top: 20px;">
                                <h2>Dodatkowe wpisy</h2>
                                <input
                                    type="text"
                                    placeholder="Klucz wpisu..."
                                    value={newEntryName}
                                    onChange={e => setNewEntryName(e.currentTarget.value)}
                                />
                                <button
                                    style="margin-left: 10px;"
                                    onClick={() => {
                                        if (!newEntryName) {
                                            return;
                                        }

                                        setLabel({
                                            ...label,
                                            metadata: {
                                                ...label.metadata,
                                                metadata: {
                                                    ...label.metadata?.metadata,
                                                    [newEntryName]: "",
                                                },
                                            },
                                        });
                                    }}
                                >
                                    Dodaj wpis
                                </button>
                            </div>
                            {Object.keys(label.metadata?.metadata ?? {})
                                .filter(
                                    v =>
                                        !v.startsWith("field-") &&
                                        !v.startsWith("element-") &&
                                        !v.startsWith("description-"),
                                )
                                .filter(v => Object.keys(predefinedMetadataKeys).indexOf(v) < 0)
                                .map(v => (
                                    <div style="margin-top: 10px; display: flex; align-items: center;">
                                        <label>
                                            Wpis '{v}':{" "}
                                            <LabelMetadataFieldEdit
                                                metadata={label.metadata?.metadata ?? {}}
                                                property={v}
                                                onChange={value => {
                                                    setLabel({
                                                        ...label,
                                                        metadata: {
                                                            ...label.metadata,
                                                            metadata: {
                                                                ...label.metadata?.metadata,
                                                                [v]: value,
                                                            },
                                                        },
                                                    });
                                                }}
                                            />
                                        </label>
                                        <button
                                            class="icon flat error"
                                            style="margin-left: 5px; width: 24px; height: 24px; font-size: 18px"
                                            onClick={() => {
                                                const newLabel = {
                                                    ...label,
                                                    metadata: {
                                                        ...label.metadata,
                                                        metadata: {
                                                            ...label.metadata?.metadata,
                                                        },
                                                    },
                                                };

                                                delete newLabel.metadata.metadata[v];

                                                setLabel(newLabel);
                                            }}
                                        >
                                            delete
                                        </button>
                                    </div>
                                ))}
                            <div
                                style="margin-top: 10px; padding:10px; margin-left: 10px; margin-right: 10px;"
                                class="paper-2"
                            >
                                <h1>Metadane</h1>
                                <div>
                                    <TemplateMetadataDisplay
                                        template={label.metadata}
                                        loadFromStorage={false}
                                        onChange={metadata => {
                                            setLabel({ ...label, metadata: { ...label.metadata, metadata } });
                                        }}
                                        doClear={templateMetadataDisplayClear}
                                        doSetMetadata={templateMetadataDisplaySetMetadata}
                                    />
                                </div>
                                <div style="margin-top: 10px; display: flex; justify-content: flex-end">
                                    <button
                                        type="button"
                                        class="info"
                                        onClick={async () => {
                                            await navigator.clipboard.writeText(
                                                Object.entries(label.metadata?.metadata ?? {})
                                                    .map(([key, value]) => `${key}=${value}`)
                                                    .join("\n"),
                                            );
                                            showSnackbarCommand.run("Skopiowano", "info", "info", 2000);
                                        }}
                                    >
                                        <span class="material-icons" style="font-size: 17px; margin-right: 5px">
                                            content_copy
                                        </span>
                                        Kopiuj
                                    </button>
                                    <button
                                        type="button"
                                        style="margin-left: 10px;"
                                        class="error"
                                        onClick={() => templateMetadataDisplayClear.run()}
                                    >
                                        Wyczyść
                                    </button>
                                </div>
                                <LabelMetadataEditorHelp />
                            </div>
                            <div style="margin-top: 10px;">
                                <h2>Drukowanie</h2>
                            </div>
                            <div style="padding: 0 10px;">
                                <TemplatePrint
                                    template={testLabelFile ?? label.path}
                                    fields={{}}
                                    overrideMetadata={
                                        testLabelFile ? { ...labelMetadata, ...label.metadata?.metadata } : undefined
                                    }
                                    overrideFields={{ ...labelMetadata, ...label.metadata?.metadata }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {label.id ? (
                    <div class="mt-2 flex flex-row">
                        <span
                            class={!childLabels || childLabels.length > 0 ? "tooltip" : ""}
                            data-tooltip="Nie można edytować szablonu jeśli zostały utworzone za pomocą niego naklejki"
                        >
                            <button
                                type="button"
                                class={!childLabels || childLabels.length > 0 ? "disabled" : ""}
                                onClick={async () => {
                                    try {
                                        await editTemplateLabel(label);
                                    } catch (ex) {
                                        console.error(ex);
                                        showSnackbarCommand.run(
                                            "Wystąpił błąd podczas edytowania naklejki",
                                            "error",
                                            "error",
                                        );
                                        return;
                                    }

                                    route("/label/template", true);
                                }}
                            >
                                Zastosuj
                            </button>
                        </span>
                        <span class="grow"></span>
                        <span
                            class={!childLabels || childLabels.length > 0 ? "tooltip" : ""}
                            data-tooltip="Nie można usunąć szablonu jeśli zostały utworzone za pomocą niego naklejki"
                        >
                            <button
                                type="button"
                                class={"error" + (!childLabels || childLabels.length > 0 ? " disabled" : "")}
                                onClick={async () => {
                                    try {
                                        await deleteTemplateLabel(label.id);
                                    } catch (ex) {
                                        console.error(ex);
                                        showSnackbarCommand.run(
                                            "Wystąpił błąd podczas usuwania naklejki",
                                            "error",
                                            "error",
                                        );
                                        return;
                                    }

                                    route(
                                        "/label/template?successMessage=" +
                                            encodeURIComponent("Szablon został usunięty"),
                                        true,
                                    );
                                }}
                            >
                                Usuń
                            </button>
                        </span>
                    </div>
                ) : (
                    <div style="margin-top: 10px;">
                        <button
                            type="button"
                            onClick={async () => {
                                try {
                                    await createTemplateLabel(label);
                                } catch (ex) {
                                    console.error(ex);
                                    showSnackbarCommand.run(
                                        "Wystąpił błąd podczas edytowania naklejki",
                                        "error",
                                        "error",
                                    );
                                    return;
                                }

                                route("/label/template", true);
                            }}
                        >
                            Utwórz szablon naklejki
                        </button>
                    </div>
                )}
            </div>
            {props.id && label ? (
                <div class="paper" style="padding: 10px; margin: 0px 10px; margin-bottom: 10px;">
                    <h1>Naklejki, które wykorzystują ten szablon</h1>
                    <div>
                        <table class="list selectable">
                            <thead>
                                <th>Nazwa</th>
                                <th>Wersja</th>
                                <th>Rejestrujący</th>
                                <th>Data rejestracji</th>
                            </thead>
                            <tbody>
                                {childLabels &&
                                    childLabels.map(v => (
                                        <tr class="item" onClick={() => route("/label/" + v.id)}>
                                            <td>{v.name}</td>
                                            <td>{v.revision}</td>
                                            <td>{v.creator.email}</td>
                                            <td>{parseToDateAndFormatIfExists(v.createdAt)}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
